import { useCallback, useEffect, useRef, useState } from "react";
import { API, apiFetch } from "../../../pages/admin/_shared";
import { useAdminNotificationsContext } from "./AdminNotificationsContext";

/**
 * Danh sách thông báo phân trang theo con trỏ + các thao tác (đọc/chưa đọc/ẩn/hàng loạt).
 * Mọi thao tác cập nhật giao diện trước (lạc quan) và hoàn tác nếu API lỗi.
 * Tự tải lại khi có thông báo mới (context.version đổi).
 */
export function useNotificationFeed({ filter = "all", type = "", q = "", pageSize = 20, enabled = true } = {}) {
  const { setUnread, version } = useAdminNotificationsContext();
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [typeCounts, setTypeCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const reqId = useRef(0);

  const buildUrl = useCallback(
    (cursor) => {
      const p = new URLSearchParams({ limit: String(pageSize), filter });
      if (type) p.set("type", type);
      if (q.trim()) p.set("q", q.trim());
      if (cursor) p.set("cursor", cursor);
      return `${API}/notifications/admin?${p.toString()}`;
    },
    [filter, type, q, pageSize]
  );

  const reload = useCallback(
    async ({ silent = false } = {}) => {
      const id = ++reqId.current;
      if (!silent) setLoading(true);
      setError("");
      try {
        const res = await apiFetch(buildUrl());
        if (id !== reqId.current) return;
        setItems(res.items || []);
        setNextCursor(res.nextCursor || null);
        if (res.typeCounts) setTypeCounts(res.typeCounts);
        setUnread(res.unread);
      } catch (e) {
        if (id === reqId.current) setError(e?.message || "Không tải được thông báo");
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    },
    [buildUrl, setUnread]
  );

  // Gõ tìm kiếm: chờ 300ms rồi mới gọi
  useEffect(() => {
    if (!enabled) return undefined;
    const t = setTimeout(() => reload(), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [enabled, reload, q]);

  // Có thông báo mới (poll phát hiện) → làm mới danh sách mà không nhấp nháy loading
  const firstVersion = useRef(version);
  useEffect(() => {
    if (!enabled || version === firstVersion.current) return;
    firstVersion.current = version;
    reload({ silent: true });
  }, [version, enabled, reload]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await apiFetch(buildUrl(nextCursor));
      setItems((prev) => {
        const seen = new Set(prev.map((x) => x._id));
        return [...prev, ...(res.items || []).filter((x) => !seen.has(x._id))];
      });
      setNextCursor(res.nextCursor || null);
    } catch (e) {
      setError(e?.message || "Không tải thêm được");
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, buildUrl]);

  /** Áp dụng thay đổi cục bộ rồi gọi API; lỗi thì khôi phục danh sách cũ. */
  const mutate = useCallback(
    async (applyLocal, request) => {
      const before = items;
      setItems((prev) => applyLocal(prev));
      try {
        const res = await request();
        if (res?.unread != null) setUnread(res.unread);
        return res;
      } catch (e) {
        setItems(before);
        setError(e?.message || "Thao tác không thành công");
        return null;
      }
    },
    [items, setUnread]
  );

  const markRead = useCallback(
    (id) => {
      if (items.find((x) => x._id === id)?.read !== false) return Promise.resolve();
      return mutate(
        (prev) => prev.map((x) => (x._id === id ? { ...x, read: true } : x)),
        () => apiFetch(`${API}/notifications/admin/${id}/read`, { method: "PATCH" })
      );
    },
    [items, mutate]
  );

  const markUnread = useCallback(
    (id) =>
      mutate(
        (prev) => prev.map((x) => (x._id === id ? { ...x, read: false } : x)),
        () => apiFetch(`${API}/notifications/admin/${id}/unread`, { method: "POST" })
      ),
    [mutate]
  );

  const dismiss = useCallback(
    (id) =>
      mutate(
        (prev) => prev.filter((x) => x._id !== id),
        () => apiFetch(`${API}/notifications/admin/${id}`, { method: "DELETE" })
      ),
    [mutate]
  );

  const bulk = useCallback(
    (ids, action) => {
      const set = new Set(ids);
      const local =
        action === "dismiss"
          ? (prev) => prev.filter((x) => !set.has(x._id))
          : (prev) => prev.map((x) => (set.has(x._id) ? { ...x, read: action === "read" } : x));
      return mutate(local, () =>
        apiFetch(`${API}/notifications/admin/bulk`, { method: "POST", body: JSON.stringify({ ids, action }) })
      );
    },
    [mutate]
  );

  const markAllRead = useCallback(
    () =>
      mutate(
        (prev) => (filter === "unread" ? [] : prev.map((x) => ({ ...x, read: true }))),
        () => apiFetch(`${API}/notifications/admin/read-all`, { method: "POST" })
      ),
    [mutate, filter]
  );

  const clearRead = useCallback(async () => {
    const res = await mutate(
      (prev) => prev.filter((x) => !x.read),
      () => apiFetch(`${API}/notifications/admin/clear-read`, { method: "POST" })
    );
    if (res) reload({ silent: true });
    return res;
  }, [mutate, reload]);

  return {
    items,
    typeCounts,
    loading,
    loadingMore,
    error,
    hasMore: Boolean(nextCursor),
    reload,
    loadMore,
    markRead,
    markUnread,
    dismiss,
    bulk,
    markAllRead,
    clearRead,
  };
}
